import React, { useState, useEffect, useCallback, useRef } from "react";
import { Form, Input, Col, Row, Button, message } from "antd";
import _ from "lodash";
import { BASE_PADDING } from "constants/Layout";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { urlRegexValidator } from "utils/formUtils";
import ModalUtil from "utils/modalUtil";
import SelectWithPaginatedData from "components/SelectWithPaginatedData";
import AutoMappingSettingsForm, { GIT_TOOL_MAPPING_CONFIG } from "../common/AutoMappingSettingsForm";


const BitBucketConfigOauthForm = ({ afterSubmit, initialValues = {} }) => {
  const featureFlags = useFeatureFlag();
  const toolConfigs = useRef();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const onSettingsChange = useCallback((changedSetting, settings) => {
    toolConfigs.current = settings;
  }, []);

  const saveSettings = useCallback(async (id) => {
    let configs = toolConfigs.current;
    const payload = {
        subProductMappingConfig: featureFlags.SIMPLIFIED_ONBOARDING ?
          _.filter([
            GIT_TOOL_MAPPING_CONFIG.MAP_INACTIVE_REPOS,
            GIT_TOOL_MAPPING_CONFIG.MAP_DORMANT_REPOS
          ], (entry) => configs?.[entry]) : [],
          codeClassificationEnabled: configs?.codeClassificationEnabled
      }
  
    await ToolsIntegrationsAPI.getGithubInstallationConfigs(id, payload).promise;
  }, [featureFlags.SIMPLIFIED_ONBOARDING]);

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [initialValues, form]);

  const onSave = async (values) => {
    setSubmitting(true);
  
    const formData = { ...values };
  
    const { promise, cancel } = ToolsIntegrationsAPI.createGitInstallation({
      id: initialValues.installationId,
      name: formData?.name,
      hostUrl: formData?.hostUrl,
      redirectUrl: initialValues.redirectUrl,
      secretId: formData?.clientId,
      secretKey: formData?.clientSecret,
      repoType: "BITBUCKET",
      authenticationType: "OAUTH_ON_PREM",
      workspace: formData?.workspace,
    });
  
    promise
      .then(async (res) => {
        if (!_.isEmpty(res)) {
          await saveSettings(res.id);
  
          message.success("Successfully configured.");
          afterSubmit && afterSubmit(res);
          ModalUtil.closeAll();
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setSubmitting(false);
      });
  
    return cancel;
  };
  

  const { hostUrl, clientId, clientSecret, installationId, redirectUrl } = initialValues;

  const getWorkspaceOptions = useCallback((params) => {
      const workspaceParams = {
        hostUrl,
        secretId: clientId,
        secretKey : clientSecret,
        id: installationId,
        redirectUrl,
        repositoryType: "BITBUCKET",
        authenticationType: "OAUTH_ON_PREM",
      };

      const finalParams = {...params, ...workspaceParams};
      const { promise, cancel } = ToolsIntegrationsAPI.postWorkSpaces(finalParams);
      return {
        cancel,
        promise: promise.then((response) => {
          return response;
        }),
      };
    },
    [hostUrl, clientId, clientSecret, installationId, redirectUrl]
  );

  return (
    <Form
      initialValues={initialValues}
      form={form}
      layout="vertical"
      onFinish={onSave}
    >
      <Row gutter={BASE_PADDING}>
        <Col span={24}>
          <Form.Item
            name="name"
            label="Name"
            rules={[
              {
                required: true,
                message: "Name is required",
              },
            ]}
          >
            <Input placeholder="Enter Configuration Name" />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name="hostUrl"
            label="Host URL"
            rules={[
              {
                required: true,
                message: "Host Url is required",
              },
              urlRegexValidator(),
            ]}
          >
            <Input
              placeholder="Enter Host Url"
              disabled={!!initialValues.hostUrl}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name="clientId"
            label="Client ID"
            rules={[
              {
                required: true,
                message: "Client ID is required",
              },
            ]}
          >
            <Input
              placeholder="Enter Client ID"
              disabled={!!initialValues.clientId}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name="clientSecret"
            label="Client Secret"
            rules={[
              {
                required: true,
                message: "Client Secret is required",
              },
            ]}
          >
            <Input
              placeholder="Enter Client Secret"
              disabled={!!initialValues.clientSecret}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name="workspace"
            label="Workspace"
            rules={[
              {
                required: true,
                message: "Workspace is required",
              },
            ]}
          >
            { hostUrl && clientId && clientSecret && redirectUrl && installationId &&
            <SelectWithPaginatedData
              api={getWorkspaceOptions}
              className="meta-data-select-menu"
              parseSelectOptions={bitbucketWorkspaceOptions}
              placeholder="Select Workspace"
            />    
            }
          </Form.Item>
        </Col>
        <AutoMappingSettingsForm onChange={onSettingsChange} />
        <Col span={24}>
          <Button
            htmlType="submit"
            type="primary"
            loading={submitting}
            disabled={submitting}
          >
            Submit
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default BitBucketConfigOauthForm;

function bitbucketWorkspaceOptions(options) {
  return options.map((option) => ({
    label: _.last(option.split("/")),
    value: option,
  }));
}
